import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../common/Toast';

const PaymentSuccess = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [plan, setPlan] = useState('');
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Récupérer les données de la transaction
        const state = location.state || {};
        const pendingTransaction = sessionStorage.getItem('aeii_pending_payment');
        
        let transactionData = state;
        if (pendingTransaction) {
          const data = JSON.parse(pendingTransaction);
          transactionData = { ...transactionData, ...data };
        }

        setPlan(transactionData.plan || '');
        setAmount(transactionData.amount || 0);

        // Récupérer la référence depuis l'URL
        const params = new URLSearchParams(window.location.search);
        const ref = params.get('ref') || transactionData.transactionId;

        if (ref) {
          // Vérifier le statut du paiement
          const response = await fetch(`http://localhost:5000/api/payment/status/${ref}`);
          const data = await response.json();

          if (data.status === 'completed') {
            await refreshUser();
            setPaymentStatus('success');
            showToast('🎉 Paiement confirmé ! Abonnement activé');
            sessionStorage.removeItem('aeii_pending_payment');
          } else if (data.status === 'pending') {
            setPaymentStatus('pending');
            // Vérifier à nouveau après 3 secondes
            setTimeout(() => {
              verifyPayment();
            }, 3000);
          } else {
            setPaymentStatus('failed');
          }
        } else {
          // Pas de référence, considérer comme succès si on arrive ici
          setPaymentStatus('success');
          sessionStorage.removeItem('aeii_pending_payment');
        }
      } catch (error) {
        console.error('Erreur:', error);
        setPaymentStatus('error');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location.state, refreshUser]);

  const getPlanLabel = (planKey) => {
    const labels = {
      classique: 'Classique',
      premium: 'Premium'
    };
    return labels[planKey] || planKey;
  };

  if (loading) {
    return (
      <section className="section">
        <div className="container text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ padding: '60px 30px' }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#e0b04c' }}></i>
            <h2 style={{ marginTop: '20px' }}>Vérification du paiement...</h2>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="section">
      <div className="container text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
        {paymentStatus === 'success' ? (
          <div style={{ 
            padding: '60px 30px',
            background: '#f0fff4',
            borderRadius: '16px',
            border: '1px solid #c6f6d5'
          }}>
            <i className="fa-solid fa-circle-check" style={{ fontSize: '4rem', color: '#48bb78' }}></i>
            <h2 style={{ marginTop: '20px', color: '#22543d' }}>Paiement réussi ! 🎉</h2>
            <p style={{ color: '#2f855a', fontSize: '1.1rem' }}>
              Félicitations ! Vous êtes maintenant abonné à la formule <strong>{getPlanLabel(plan)}</strong>.
            </p>
            <div style={{ 
              margin: '20px 0',
              padding: '16px',
              background: 'rgba(255,255,255,0.7)',
              borderRadius: '8px'
            }}>
              <p style={{ margin: 0, color: '#4a5568' }}>
                Montant payé : <strong style={{ color: '#0c2340' }}>{amount.toLocaleString()} FCFA</strong>
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/mon-compte" className="btn btn-dark">
                <i className="fa-solid fa-user"></i> Mon compte
              </Link>
              <Link to="/publications" className="btn btn-primary">
                <i className="fa-solid fa-book"></i> Explorer les publications
              </Link>
              <Link to="/abonnements" className="btn btn-ghost">
                <i className="fa-solid fa-arrow-left"></i> Voir les abonnements
              </Link>
            </div>
          </div>
        ) : paymentStatus === 'pending' ? (
          <div style={{ 
            padding: '60px 30px',
            background: '#ebf8ff',
            borderRadius: '16px',
            border: '1px solid #bee3f8'
          }}>
            <i className="fa-solid fa-clock" style={{ fontSize: '4rem', color: '#3182ce' }}></i>
            <h2 style={{ marginTop: '20px', color: '#2a4365' }}>En cours de validation</h2>
            <p style={{ color: '#2c5282' }}>
              Votre paiement est en cours de traitement. Cela peut prendre quelques instants.
            </p>
            <div className="fa-solid fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#3182ce', marginTop: '20px' }}></div>
            <Link to="/mon-compte" className="btn btn-primary" style={{ marginTop: '20px' }}>
              Voir mon compte
            </Link>
          </div>
        ) : (
          <div style={{ 
            padding: '60px 30px',
            background: '#fff5f5',
            borderRadius: '16px',
            border: '1px solid #fed7d7'
          }}>
            <i className="fa-solid fa-circle-xmark" style={{ fontSize: '4rem', color: '#fc8181' }}></i>
            <h2 style={{ marginTop: '20px', color: '#742a2a' }}>Paiement non confirmé</h2>
            <p style={{ color: '#9b2c2c' }}>
              Nous n'avons pas pu confirmer votre paiement. Veuillez réessayer ou contacter le support.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
              <Link to="/abonnements" className="btn btn-dark">
                <i className="fa-solid fa-arrow-left"></i> Réessayer
              </Link>
              <Link to="/contact" className="btn btn-ghost">
                <i className="fa-solid fa-envelope"></i> Contacter le support
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PaymentSuccess;